import {
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  numericColumn,
  RowStyles,
  simpleHeader,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { dateCell, markupCell, Price, priceCell } from "src/components";
import { InvoiceStatus } from "src/generated/graphqlTypes";
import {
  mapToProjectStagesFilter,
  SingleStageFilter,
  useProjectStageSingleFilter,
} from "src/hooks/useProjectStageSingleFilter";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import {
  HomeownerContractChangeOrderSummaryFragment,
  HomeownerContractSummaries_InvoiceFragment,
  HomeownerContractSummaries_ProjectItemAllocationFragment,
  HomeownerContractSummariesQuery,
  HomeownerContractSummaryFragment,
  HomeownerContractSummaryWithChangeOrdersFragment,
  useHomeownerContractSummariesQuery,
} from "src/routes/projects/homeowner-contracts/components/HomeownerContractsTable.generated";
import { createBudgetUrl, createHomeownerContractChangeOrderUrl, createHomeownerContractsUrl } from "src/RouteUrls";
import { formatCentsToPrice } from "src/utils";

type HomeownerContractsTableProps = { projectId: string; showSharedCosts: boolean };

export function HomeownerContractsTable(props: HomeownerContractsTableProps) {
  const { projectId, showSharedCosts } = props;
  const { latestActiveStage } = useProjectContext();
  const { filterDefs } = useProjectStageSingleFilter(latestActiveStage);
  const layoutState = useGridTableLayoutState<SingleStageFilter>({
    persistedFilter: { filterDefs, storageKey: `homeownerContractsFilter_${projectId}` },
  });
  const query = useHomeownerContractSummariesQuery({
    variables: { projectId, stagesFilter: mapToProjectStagesFilter(layoutState.filter), showSharedCosts },
    // We're using cache-and-network to re-fetch contracts after adding one via the Specs & Selections workflow
    nextFetchPolicy: "cache-and-network",
  });
  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const createTableRows = useCallback((data: HomeownerContractSummariesQuery | undefined) => {
    const project = data?.project;
    if (!project) return [];
    const contracts = project.stages.flatMap((s) => s.homeownerContracts);
    const allocations = project.projectItemAllocations ?? [];
    const invoices = project.stages.flatMap((s) => s.invoices);
    return createRows(contracts, allocations, sumContracts(contracts, allocations, invoices));
  }, []);

  return (
    <GridTableLayout
      layoutState={layoutState}
      tableProps={{
        query,
        createRows: createTableRows,
        columns,
        rowStyles,
        sorting: { on: "client" },
        style: { rowHeight: "fixed" },
        emptyState: { title: query.error ? query.error.message : emptyMessage },
      }}
    />
  );
}

type HeaderRow = { kind: "header" };
type TotalsRow = {
  kind: "totals";
  data: {
    invoicedInCents: number;
    uninvoicedInCents: number;
    amountPaidInCents: number;
    balanceInCents: number;
    budgetChangeInCents: number;
    priceChangeInCents: number;
    accruedInvoicedInCents: number;
    accruedAmountPaidInCents: number;
  };
};
type SharedCostTotalRow = {
  kind: "sharedCost";
  data: Pick<TotalsRow["data"], "budgetChangeInCents" | "priceChangeInCents">;
};
type ContractRow = { kind: "contract"; id: string; data: HomeownerContractSummaryFragment };
type ChangeOrderRow = {
  kind: "changeOrder";
  id: string;
  data: { changeOrder: HomeownerContractChangeOrderSummaryFragment; stageName: string };
};
type Row = TotalsRow | HeaderRow | ContractRow | ChangeOrderRow | SharedCostTotalRow;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "contract",
      name: "Contract",
      totals: "Totals",
      header: "Contract",
      canHide: false,
      contract: ({ name }) => name,
      changeOrder: ({ changeOrder }) => ({
        content: `CO #${changeOrder.identifier}`,
        // Coerce string-sort back to numeric sort (so "10" comes after "9" instead of after "1")
        sortValue: Number(changeOrder.identifier),
      }),
      // Position before the contract rows
      sharedCost: {
        content: "Shared Costs",
        sortValue: 0,
        tooltip: "Budget and Price allocations coming from parent projects",
      },
      mw: "240px",
    }),
    column<Row>({
      id: "stage",
      name: "Stage",
      totals: emptyCell,
      header: "Stage",
      contract: ({ projectStage }) => projectStage.stage.name,
      changeOrder: ({ stageName }) => stageName,
      sharedCost: emptyCell,
      mw: "120px",
    }),
    dateColumn<Row>({
      id: "executionDate",
      name: "Execution Date",
      totals: emptyCell,
      header: "Execution Date",
      contract: ({ executionDate }) => dateCell(executionDate),
      changeOrder: ({ changeOrder }) => dateCell(changeOrder.executionDate),
      sharedCost: emptyCell,
      mw: "140px",
    }),
    numericColumn<Row>({
      id: "budgetedCost",
      name: "Budgeted Cost",
      totals: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      header: () => "Budgeted Cost",
      contract: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.budgetChangeInCents }),
      sharedCost: ({ budgetChangeInCents }) => priceCell({ valueInCents: budgetChangeInCents }),
      mw: "140px",
    }),
    numericColumn<Row>({
      id: "markup",
      name: "Markup",
      totals: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      header: () => "Markup",
      contract: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      changeOrder: ({ changeOrder: { budgetChangeInCents, priceChangeInCents } }) =>
        markupCell(budgetChangeInCents, priceChangeInCents),
      sharedCost: ({ budgetChangeInCents, priceChangeInCents }) => markupCell(budgetChangeInCents, priceChangeInCents),
      mw: "160px",
    }),
    numericColumn<Row>({
      id: "contractPrice",
      name: "Contract Price",
      totals: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      header: () => "Contract Price",
      contract: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.priceChangeInCents }),
      sharedCost: ({ priceChangeInCents }) => priceCell({ valueInCents: priceChangeInCents }),
      mw: "140px",
    }),
    numericColumn<Row>({
      id: "invoiced",
      name: "Invoiced",
      totals: ({ invoicedInCents, accruedInvoicedInCents }) => ({
        content: <Price valueInCents={invoicedInCents} />,
        tooltip:
          accruedInvoicedInCents !== 0
            ? `Total invoiced, including ${formatCentsToPrice(accruedInvoicedInCents)} on Percentage of Completion/Soft Costs invoices`
            : "Total invoiced",
      }),
      header: () => "Invoiced",
      contract: ({ invoicedInCents }) => priceCell({ valueInCents: invoicedInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.invoicedInCents }),
      sharedCost: emptyCell,
      mw: "120px",
    }),
    numericColumn<Row>({
      id: "remainingOnContract",
      name: "Remaining on Contract",
      totals: ({ uninvoicedInCents, accruedInvoicedInCents }) => ({
        content: <Price valueInCents={uninvoicedInCents} />,
        tooltip:
          accruedInvoicedInCents !== 0
            ? `Remaining on contract, reduced by ${formatCentsToPrice(accruedInvoicedInCents)} on Percentage of Completion/Soft Costs invoices`
            : "Remaining on contract",
      }),
      header: () => "Remaining on Contract",
      contract: ({ uninvoicedInCents }) => priceCell({ valueInCents: uninvoicedInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.uninvoicedInCents }),
      sharedCost: emptyCell,
      mw: "180px",
    }),
    numericColumn<Row>({
      id: "receivedToDate",
      name: "Received to Date",
      totals: ({ amountPaidInCents, accruedAmountPaidInCents }) => {
        return {
          content: <Price valueInCents={amountPaidInCents} />,
          tooltip:
            accruedAmountPaidInCents !== 0
              ? `Total received, including ${formatCentsToPrice(accruedAmountPaidInCents)} on Percentage of Completion/Soft Costs invoices`
              : "Total received to date",
        };
      },
      header: () => "Received to Date",
      contract: ({ amountPaidInCents }) => priceCell({ valueInCents: amountPaidInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.amountPaidInCents }),
      sharedCost: emptyCell,
      mw: "140px",
    }),
    numericColumn<Row>({
      id: "balanceOutstanding",
      name: "Balance Outstanding",
      totals: ({ balanceInCents, accruedAmountPaidInCents, accruedInvoicedInCents }) => {
        const accruedBalanceInCents = accruedInvoicedInCents - accruedAmountPaidInCents;
        return {
          content: <Price valueInCents={balanceInCents} />,
          tooltip:
            accruedBalanceInCents !== 0
              ? `Balance outstanding, including ${formatCentsToPrice(accruedBalanceInCents)} on Percentage of Completion/Soft Costs invoices`
              : "Balance outstanding",
        };
      },
      header: () => "Balance Outstanding",
      contract: ({ balanceInCents }) => priceCell({ valueInCents: balanceInCents }),
      changeOrder: ({ changeOrder }) => priceCell({ valueInCents: changeOrder.balanceInCents }),
      sharedCost: emptyCell,
      mw: "160px",
    }),
  ];
}

function createRows(
  contracts: HomeownerContractSummaryWithChangeOrdersFragment[],
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
  totals: HomeownerContractTotals["data"],
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    { kind: "totals", id: "totals", data: totals },
    ...createSharedCostRows(allocations),
    ...contracts.map((contract) => {
      return {
        kind: "contract" as const,
        id: contract.id,
        data: contract,
        children: contract.changeOrders.map((co) => ({
          kind: "changeOrder" as const,
          id: co.id,
          data: { changeOrder: co, stageName: contract.projectStage.stage.name },
        })),
      };
    }),
  ];
}

function createRowStyles(projectId: string): RowStyles<Row> {
  return {
    header: {},
    contract: { rowLink: ({ id }) => createHomeownerContractsUrl(projectId, id) },
    changeOrder: { rowLink: ({ data }) => createHomeownerContractChangeOrderUrl(projectId, data.changeOrder.id) },
    sharedCost: { cellCss: Css.xsSb.$, rowLink: () => createBudgetUrl(projectId) },
  };
}

function createSharedCostRows(allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[]) {
  if (allocations.isEmpty) return [];
  return [{ kind: "sharedCost" as const, id: "sharedCost", data: sumSharedCostTotals(allocations) }];
}

type HomeownerContractTotals = Omit<TotalsRow, "kind">;

function sumContracts(
  contracts: HomeownerContractSummaryWithChangeOrdersFragment[],
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
  invoices: HomeownerContractSummaries_InvoiceFragment[],
): HomeownerContractTotals["data"] {
  const flattenedContracts = contracts.flatMap(({ changeOrders, ...contract }) => [contract, ...changeOrders]);
  const totals = flattenedContracts.reduce(
    (acc, contract) => ({
      budgetChangeInCents: acc.budgetChangeInCents + (contract.budgetChangeInCents || 0),
      priceChangeInCents: acc.priceChangeInCents + (contract.priceChangeInCents || 0),
      invoicedInCents: acc.invoicedInCents + (contract.invoicedInCents || 0),
      uninvoicedInCents: acc.uninvoicedInCents + (contract.uninvoicedInCents || 0),
      amountPaidInCents: acc.amountPaidInCents + (contract.amountPaidInCents || 0),
      balanceInCents: acc.balanceInCents + (contract.balanceInCents || 0),
      accruedInvoicedInCents: 0,
      accruedAmountPaidInCents: 0,
    }),
    {
      budgetChangeInCents: 0,
      priceChangeInCents: 0,
      invoicedInCents: 0,
      amountPaidInCents: 0,
      uninvoicedInCents: 0,
      balanceInCents: 0,
      accruedInvoicedInCents: 0,
      accruedAmountPaidInCents: 0,
    },
  );

  if (allocations.nonEmpty) {
    const allocationsTotals = sumSharedCostTotals(allocations);
    totals.priceChangeInCents += allocationsTotals.priceChangeInCents;
    totals.budgetChangeInCents += allocationsTotals.budgetChangeInCents;
  }

  const maybeAccruedInvoicesTotals = sumAccruedInvoices(invoices);
  totals.accruedInvoicedInCents = maybeAccruedInvoicesTotals.invoicedInCents;
  totals.accruedAmountPaidInCents = maybeAccruedInvoicesTotals.amountPaidInCents;
  totals.invoicedInCents += maybeAccruedInvoicesTotals.invoicedInCents;
  totals.amountPaidInCents += maybeAccruedInvoicesTotals.amountPaidInCents;
  totals.balanceInCents += maybeAccruedInvoicesTotals.balanceInCents;
  // Accrued invoiced amounts bill against the project budget, so they reduce what's still remaining on the contract
  totals.uninvoicedInCents -= maybeAccruedInvoicesTotals.invoicedInCents;

  return totals;
}

function sumSharedCostTotals(
  allocations: HomeownerContractSummaries_ProjectItemAllocationFragment[],
): SharedCostTotalRow["data"] {
  const hclias = allocations.flatMap((pia) => pia.homeownerContractLineItemAllocations);
  return {
    priceChangeInCents: hclias.sum((hclia) => hclia.priceInCents),
    budgetChangeInCents: hclias.sum((hclia) => hclia.costInCents),
  };
}

// Accrued Invoices are not accounted for in the homeowner contract invoiced amount as they are based on cost accruals
// As they are still invoiced against project budget, we need to add them to the total,
// otherwise there always will be an open balance in the project
function sumAccruedInvoices(invoices: HomeownerContractSummaries_InvoiceFragment[]) {
  const invoicedInCents = invoices.sum((i) => i.accruedAmountInCents);
  const amountPaidInCents = invoices
    .filter((i) => i.status.code === InvoiceStatus.Paid)
    .sum((i) => i.accruedAmountInCents);

  return { invoicedInCents, amountPaidInCents, balanceInCents: invoicedInCents - amountPaidInCents };
}

const emptyMessage = "There are no signed contracts for the selected stage(s).";
